import React, { ChangeEvent, useMemo, useState, useEffect, useContext } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { showNotification } from '@/utils/notificationUtils';
import { Button } from '@/components/Elements';
import { useAuth } from '@/lib/auth';
import { useAddCreditCard } from '../api/addCreditCard';
import Lottie from 'lottie-react';
import progressAnimation from '@/assets/animation/save_loader.json';
import { CreditCard } from '@/features/auth';
import './CreditCartDetails.scss';
import { Loader } from '@/components/Loader/Loader';
import { UserContext } from '@/components/Layout';
const useOptions = () => {
  const userContext = useContext(UserContext);
  const options = useMemo(
    () => ({
      style: {
        width: '100%',
        base: {
          width: '100%',
          color: '#1a1a1a',
          letterSpacing: '0.025em',
          fontFamily: 'Inter, sans-serif',
          fontSize: userContext?.isLandscape ? '1.125rem' : '16px',
          fontWeight: '500',
          '::placeholder': {
            color: '#aab7c4',
            fontFamily: 'Inter, sans-serif',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    }),
    []
  );

  return options;
};
type Props = {
  onSuccess: (paymentCard?: CreditCard) => void;
  defaultCreditCard?: CreditCard;
  addNewCard?: boolean;
  isNonRegistration?: boolean;
  isMyPayment?: boolean;
  paymentMode?: string;
};
export const CheckoutForm = ({
  onSuccess,
  defaultCreditCard,
  addNewCard,
  isNonRegistration,
  isMyPayment,
  paymentMode,
}: Props) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();
  const [cardName, setCardName] = useState('');
  const addCreditCardMutation = useAddCreditCard();
  useEffect(() => {
    if (user) {
      if (user?.firstName && user?.lastName) {
        setCardName(`${user?.firstName} ${user?.lastName}`);
      }
    }
  }, [user]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.id === 'name') {
      setCardName(event.target.value);
    }
  };

  const saveCard = async (paymentCard: CreditCard) => {
    await addCreditCardMutation.mutateAsync({
      data: {
        paymentMethodId: paymentCard.id,
        paymentMode,
      },
    });
    onSuccess(paymentCard);
  };

  const createStripeTokenForCard = async () => {
    if (!user || !user.stripeCustomerId || !elements || !stripe) {
      return;
    }
    const cardNumberElement: any = elements.getElement(CardNumberElement);
    const cardExpiryElement: any = elements.getElement(CardExpiryElement);
    const cardCvcElement: any = elements.getElement(CardCvcElement);
    if (!cardName || cardName.trim().length < 1) {
      showNotification({
        type: 'error',
        title: 'Please enter name on the card',
      });
      return false;
    } else if (cardNumberElement['_invalid']) {
      showNotification({
        type: 'error',
        title: 'Invalid card details , please enter valid card details.',
      });
      return;
    } else if (!cardNumberElement['_complete']) {
      showNotification({
        type: 'error',
        title: 'Please complete filling card details.',
      });
      return;
    } else if (cardExpiryElement['_invalid']) {
      showNotification({
        type: 'error',
        title: 'Invalid expiry details , please enter valid card expiry details.',
      });
      return;
    } else if (!cardExpiryElement['_complete']) {
      showNotification({
        type: 'error',
        title: 'Please complete filling card expiry details.',
      });
      return;
    } else if (cardCvcElement['_invalid']) {
      showNotification({
        type: 'error',
        title: 'Invalid cvv details , please enter valid cvv',
      });
      return;
    } else if (!cardCvcElement['_complete']) {
      showNotification({
        type: 'error',
        title: 'Please complete filling cvv details.',
      });
      return;
    }
    try {
      const payload: any = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: cardName,
          address: {
            city: user?.city,
            country: 'US',
            line1: user?.address,
            line2: user?.apt,
            postal_code: user?.zip,
            state: user?.state,
          },
        },
      } as any);

      if (!payload && payload.error) {
        showNotification({
          type: 'error',
          title: payload ? payload.error.message : 'Error occured while creading payment method',
        });

        return;
      }

      const { paymentMethod } = payload;
      const paymentCard: CreditCard = {
        id: paymentMethod.id,
        expireMonth: paymentMethod.card.exp_month,
        expireYear: paymentMethod.card.exp_year,
        firstName: paymentMethod.billing_details.name,
        lastName: '',
        isDefault: true,
        number: paymentMethod.card.last4,
        type: paymentMethod.card.brand,
        stripePaymentToken: paymentMethod.id
      };

      if (isNonRegistration) {
        onSuccess(paymentCard);
      } else {
        saveCard(paymentCard);
      }
    } catch (error: any) {
      showNotification({
        type: 'error',
        title: error.message,
      });
    }
  };

  return (
    <div className="credit-card-details">
      <div className="credit-card-form">
        <div className="payment-input-field">
          <div>
            <CardNumberElement options={options} />
          </div>
        </div>
        <div>
          <input
            id="name"
            type="text"
            value={cardName}
            className="payment-input-field"
            onChange={handleChange}
            placeholder="Name on the Card"
          />
        </div>
        <div className={`my-payment-div ${isMyPayment ? 'is-my-payment' : ''}`}>
          <div className="payment-input-field mr-10">
            <div>
              <CardExpiryElement options={options} />
            </div>
          </div>
          <div className="payment-input-field">
            <div>
              <CardCvcElement options={options} />
            </div>
          </div>
        </div>
      </div>

      <div className={`credit-card-form-action ${isMyPayment ? 'mt-50' : ''}`}>
        <button
          className="petsked-button button-dark"
          onClick={async () => {
            createStripeTokenForCard();
          }}
          disabled={addCreditCardMutation.isLoading}
        >
          {addCreditCardMutation.isLoading ? (
            <Loader text={'adding new card...'} />
          ) : (
            <span>{isMyPayment ? 'Add New Card' : 'Continue'}</span>
          )}
        </button>
      </div>
    </div>
  );
};
